import {Grid, Typography} from '@mui/material';
import './component.css';
import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {getAdmissionStartDate} from '../../../../util/patientUtil';
import {getConfig} from '../../../../util/configUtil';
import _ from 'lodash';
import API from '../../../../app/api';
import moment from 'moment/moment';
import {ctxCounterVisitsED, ctxCounterVisitsIP, ctxPatientType} from '../../../../store/reducers/patientContext';
import {useSelector} from 'react-redux';
import usePatientLocation from '../../../../hooks/usePatientLocation';
import {PATIENT_CONTEXT_TYPE} from '../../../../util/patientContextUtil';
import BookedPatientDetails from './bookedPatientDetails';
import {activityTypeEDDict, attendanceSource} from '../../../../store/reducers/dicts';

const ActivityDemographic = ({patient}) => {
    const {t} = useTranslation();

    const [location] = usePatientLocation(patient);
    const [isED, setIsED] = useState(false);

    // Configs
    const defaultDateTimeFormat = getConfig('defaultDateTimeFormat');

    // Selectors
    const edVisits = useSelector(ctxCounterVisitsED);
    const ipVisits = useSelector(ctxCounterVisitsIP);
    const patientType = useSelector(ctxPatientType);
    const types = useSelector(activityTypeEDDict);
    const attSourceDict = useSelector(attendanceSource);

    const [mergedObject, setMergedObject] = useState(null);
    const [username, setUsername] = useState(null);
    const [mergedUsername, setMergedUsername] = useState(null);
    const isReview = patientType === PATIENT_CONTEXT_TYPE.REVIEW;

    useEffect(() => {
        if (patient?.admission?.createdBy) {
            let createdBy = patient?.admission?.createdBy;
            if (hasSingleSlash(createdBy)) {
                createdBy = createdBy.replace(/\\/g, '\\\\');
            }

            createdBy = createdBy.replace(/'/g, '\\\'');
            getFullUserName(createdBy, setUsername);

            if (!_.startsWith(patient?.patient?.__id, 'AGT')) {
                API.getMergesList(null, patient?.patient?.__id)
                    .then(res => {
                        if (!_.isEmpty(res?.__data)) {
                            const filtered = _.filter(res?.__data, r => moment(r?.lastModifiedDate).isAfter(moment(patient?.admission?.startDate)));
                            setMergedObject(_.last(filtered) || null);
                        }
                    });
            }
        } else {
            setUsername(null);
        }
    }, [patient]);

    useEffect(() => {
        if (mergedObject?.lastModifiedBy) {
            let createdBy = mergedObject?.lastModifiedBy;
            if (hasSingleSlash(createdBy)) {
                createdBy = createdBy.replace(/\\/g, '\\\\');
            }

            getFullUserName(createdBy, setMergedUsername);
        }
    }, [mergedObject]);

    useEffect(() => {
        if (location) {
            setIsED(location?.wardType === 'ED' || patient?.wardId === 'ED');
        }
    }, [location, patient]);

    function hasSingleSlash(inputString) {
        const parts = inputString?.split('/');
        return parts?.length === 1;
    }

    function getFullUserName(createdBy, setMethod) {
        API.getUsers(`?$filter=${encodeURIComponent(`userName eq '${createdBy}`)}`)
            .then(res => {
                if (!_.isEmpty(res?.data?.__data)) {
                    const user = res?.data?.__data[0];
                    setMethod(user?.fullName || null);
                } else {
                    setMethod(null);
                }
            })
            .catch(err => {
                console.error(err);
                setMethod(null);
            });
    }

    const getUserName = (merged) => {
        if (!merged && patient?.admission?.createdBy) {
            return username ? `${username} (${patient?.admission?.createdBy})` : patient?.admission?.createdBy;
        } else if (merged && mergedObject?.lastModifiedBy) {
            return mergedUsername ? `${mergedUsername} (${mergedObject?.lastModifiedBy})` : mergedObject?.lastModifiedBy;
        }
        return '-';
    };

    const mapActivityType = (code) => {
        const type = types.find(t => t.__code === code);
        if (!type) return '-';
        return `${type?.__description} (${type?.__code})`;
    };

    const getAttendanceSourceValue = () => {
        const sourceCode = patient?.admission?.admitSourceCode;
        if (!sourceCode) return '-';

        const sourceType = _.find(attSourceDict, { snomedCode: sourceCode });

        return sourceType?.snomedPreferredTerm || patient?.admission?.admitSourceDescription;
    }

    return (
        <Grid container spacing={1} columns={9} className="container" alignContent="flex-start">
            {!isReview && (
                <>
                    <Grid item xs={3} className="container-grid">
                        <Typography variant="body1" className="title">
                            {t('Date of Registration')}
                        </Typography>

                        <Typography variant="body1" className="body">
                            {getAdmissionStartDate(patient?.admission).format(defaultDateTimeFormat)}
                        </Typography>
                    </Grid>

                    <Grid item xs={3} className="container-grid">
                        <Typography variant="body1" className="title">
                            {t(mergedObject ? 'Registered Temporary Activity' : 'Registered Activity')}
                        </Typography>

                        <Typography variant="body1" className="body">
                            {getUserName(false)}
                        </Typography>
                    </Grid>

                    {mergedObject &&
                        <Grid item xs={3} className="container-grid">
                            <Typography variant="body1" className="title">
                                {t('Registered Activity')}
                            </Typography>

                            <Typography variant="body1" className="body">
                                {getUserName(true)}
                            </Typography>
                        </Grid>
                    }

                    <Grid item xs={3} className="container-grid">
                        <Typography variant="body1" className="title">
                            {t('Visit Number')}
                        </Typography>

                        <Typography variant="body1" className="body">
                            {patient?.admission?.attendanceId || '-'}
                        </Typography>
                    </Grid>

                    <Grid item xs={3} className="container-grid">
                        <Typography variant="body1" className="title">
                            {t('Attendance Source')}
                        </Typography>

                        <Typography variant="body1" className="body">
                            {getAttendanceSourceValue() || '-'}
                        </Typography>
                    </Grid>

                    <Grid item xs={3} className="container-grid">
                        <Typography variant="body1" className="title">
                            {t('Arrival Mode')}
                        </Typography>

                        <Typography variant="body1" className="body">
                            {patient?.admission?.arrivalModeDescription || '-'}
                        </Typography>
                    </Grid>
                </>
            )}

            <Grid item xs={3} className="container-grid">
                <Typography variant="body1" className="title">
                    {t('ED Visits last 12m')}
                </Typography>

                <Typography variant="body1" className="body">
                    {edVisits ?? 0}
                </Typography>
            </Grid>

            <Grid item xs={3} className="container-grid">
                <Typography variant="body1" className="title">
                    {t('Inpatient Visits last 12m')}
                </Typography>

                <Typography variant="body1" className="body">
                    {ipVisits ?? 0}
                </Typography>
            </Grid>

            {!isReview && (
                <Grid item xs={3} className="container-grid">
                    <Typography variant="body1" className="title">
                        {t('Consent to being contacted by the Trust to give feedback')}
                    </Typography>

                    <Typography variant="body1" className="body">
                        {patient?.metadata?.feedback ? t('Yes') : t('No')}
                    </Typography>
                </Grid>
            )}

            {isReview && <BookedPatientDetails patient={patient}/>}

            {isED &&
                <Grid item xs={12} className="container-grid">
                    <Typography variant="body1" className="title">
                        {t('Emergency Care Attendance Category')}
                    </Typography>

                    <Typography variant="body1" className="body">
                        {patient?.metadata?.attendanceCategory?.description || '-'}
                    </Typography>
                </Grid>
            }

            {(isED && patient?.admission?.edActivityType) &&
                <Grid item xs={12} className="container-grid">
                    <Typography variant="body1" className="title">
                        {t('Emergency Service Activity Type')}
                    </Typography>

                    <Typography variant="body1" className="body">
                        {mapActivityType(patient?.admission?.edActivityType)}
                    </Typography>
                </Grid>
            }
        </Grid>
    );
};

export default React.memo(ActivityDemographic);